// Webflow Lottie element ([data-animation-type="lottie"]), rendered with lottie-web.
// Elements marked `data-is-ix2-target="1"` were driven by Webflow interactions instead of
// autoplaying; they register with the nearest LottieGroup so an interaction can play them.

import type { AnimationItem } from 'lottie-web';
import { createContext, useContext, useEffect, useRef } from 'react';
import type { WfProps } from './element';

export type LottieRegistry = (el: HTMLElement, anim: AnimationItem) => () => void;
export const LottieRegistryContext = createContext<LottieRegistry | null>(null);

export function Lottie({ as: Tag = 'div', ...props }: WfProps) {
  const ref = useRef<HTMLElement>(null);
  const register = useContext(LottieRegistryContext);
  const src = props['data-src'];
  const loop = props['data-loop'] === '1';
  const ixTarget = props['data-is-ix2-target'] === '1';
  const autoplay = props['data-autoplay'] === '1' && !ixTarget;
  const direction = props['data-direction'] === '-1' ? -1 : 1;
  const renderer = (props['data-renderer'] ?? 'svg') as 'svg' | 'canvas';

  useEffect(() => {
    const el = ref.current;
    if (!el || !src) return undefined;
    let anim: AnimationItem | undefined;
    let unregister: (() => void) | undefined;
    let disposed = false;
    void import('lottie-web/build/player/lottie_light').then(({ default: lottie }) => {
      if (disposed) return;
      anim = lottie.loadAnimation({ container: el, renderer, loop, autoplay, path: src });
      anim.setDirection(direction);
      if (register) unregister = register(el, anim);
    });
    return () => {
      disposed = true;
      unregister?.();
      anim?.destroy();
    };
  }, [src, loop, autoplay, direction, renderer, register]);

  return <Tag ref={ref} {...props} />;
}
